import React, { useEffect, useState, useCallback } from "react";
import { offersAPI } from "../api/client.js";
import { toast } from "react-toastify";
import "./OfferDocuments.css";
import { TableSkeleton } from "../components/LoadingSkeleton";
import EmptyState from "../components/EmptyState";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const openDocument = async (offerId, docType, label) => {
  try {
    const res = await offersAPI.downloadDocument(offerId, docType);
    const url = res.data?.downloadUrl;
    if (!url) {
      toast.info(`No ${label} available`);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (err) {
    toast.error(err?.response?.data?.error || `Failed to open ${label}`);
  }
};

export default function OfferDocuments() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await offersAPI.getAllDocuments();
      setOffers(res.data?.offers || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const q = search.trim().toLowerCase();
  const filtered = offers.filter((o) => {
    if (!q) return true;
    return [
      o.candidate_name,
      o.position_offered,
      o.business_unit,
      o.division,
      o.department,
      String(o.offer_id),
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  return (
    <div className="odoc-container">
      <div className="odoc-card">
        <div className="odoc-header">
          <h2>Offer Documents (PAN / Aadhaar)</h2>
          <p>View and download PAN &amp; Aadhaar cards uploaded against offers.</p>
        </div>

        <div className="odoc-toolbar">
          <input
            type="text"
            className="odoc-search"
            placeholder="Search by candidate, role, unit, division..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="odoc-refresh" onClick={fetchDocs} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 24 }}>
            <TableSkeleton rows={5} cols={5} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<DescriptionOutlinedIcon style={{ fontSize: 48, color: "#94A3B8" }} />}
            title="No documents found"
            message="Upload PAN / Aadhaar cards while creating an offer. They will appear here."
          />
        ) : (
          <div className="odoc-table-wrap">
            <table className="odoc-table">
              <thead>
                <tr>
                  <th scope="col">Offer ID</th>
                  <th scope="col">Candidate</th>
                  <th scope="col">Position</th>
                  <th scope="col">Unit / Division</th>
                  <th scope="col">PAN Card</th>
                  <th scope="col">Aadhaar Card</th>
                  <th scope="col">Offer Letter</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.offer_id}>
                    <td>{o.offer_id}</td>
                    <td>{o.candidate_name || "-"}</td>
                    <td>{o.position_offered || "-"}</td>
                    <td>
                      {[o.business_unit, o.division, o.department]
                        .filter(Boolean)
                        .join(" / ") || "-"}
                    </td>
                    <td>
                      {o.pan_card_url ? (
                        <button
                          className="odoc-link"
                          onClick={() => openDocument(o.offer_id, "pan", "PAN card")}
                        >
                          View / Download
                        </button>
                      ) : (
                        <span className="odoc-absent">Not uploaded</span>
                      )}
                    </td>
                    <td>
                      {o.aadhaar_card_url ? (
                        <button
                          className="odoc-link"
                          onClick={() =>
                            openDocument(o.offer_id, "aadhaar", "Aadhaar card")
                          }
                        >
                          View / Download
                        </button>
                      ) : (
                        <span className="odoc-absent">Not uploaded</span>
                      )}
                    </td>
                    <td>
                      {o.offer_letter_url ? (
                        <button
                          className="odoc-link"
                          onClick={() =>
                            openDocument(o.offer_id, "offer_letter", "offer letter")
                          }
                        >
                          View / Download
                        </button>
                      ) : (
                        <span className="odoc-absent">Not released</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
